import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GraduationCap, Eye, EyeSlash, LockKey } from "@phosphor-icons/react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error(t("auth.register.errors.passwordShort"));
    if (pw !== confirm) return toast.error(t("auth.register.errors.passwordMismatch"));
    if (!token) return toast.error(t("auth.reset.errors.tokenMissing"));
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success(t("auth.reset.success"));
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-6 relative" data-testid="reset-page">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <GraduationCap size={22} weight="duotone" />
          </div>
          <div>
            <div className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Bilinguist</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">School OS</div>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t("auth.reset.header")}</div>
          <h2 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("auth.reset.title")}</h2>
          <p className="text-sm text-muted-foreground mt-2">{t("auth.reset.subtitle")}</p>
        </div>

        {!token && (
          <div className="border border-destructive/40 bg-destructive/5 text-destructive text-sm p-3 rounded-md">
            {t("auth.reset.errors.tokenMissing")}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="pw" className="text-xs font-semibold uppercase tracking-wider">{t("auth.reset.newPassword")}</Label>
            <div className="relative mt-1.5">
              <Input id="pw" data-testid="reset-password" type={show ? "text" : "password"} required
                value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder="••••••••" className="h-11 pr-10" />
              <button type="button" onClick={() => setShow((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {show ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="confirm" className="text-xs font-semibold uppercase tracking-wider">{t("auth.register.fields.confirmPassword")}</Label>
            <Input id="confirm" data-testid="reset-confirm" type={show ? "text" : "password"} required
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••" className="mt-1.5 h-11" />
          </div>
          <Button data-testid="reset-submit" type="submit" disabled={busy || !token} className="w-full h-11 rounded-full font-semibold gap-2">
            <LockKey size={16} weight="bold" />
            {busy ? t("common.saving") : t("auth.reset.saveBtn")}
          </Button>
          <Link to="/login" className="block text-center text-xs text-muted-foreground hover:text-primary">
            {t("auth.forgot.backToLogin")}
          </Link>
        </form>
      </div>
    </div>
  );
}
